import React, { forwardRef } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const sizes = {
    xs: {
        outer: "14px",
        inner: "6px",
        fontSize: tokens.typography.fontSize["md-12px"],
        lineHeight: "16px",
        gap: "4px",
    },
    sm: {
        outer: "16px",
        inner: "8px",
        fontSize: tokens.typography.fontSize["md-12px"],
        lineHeight: "16px",
        gap: "6px",
    },
    md: {
        outer: "18px",
        inner: "10px",
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
        gap: "8px",
    },
    lg: {
        outer: "20px",
        inner: "12px",
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
        gap: "8px",
    },
};

const defaultFocusVisibleState = {
    outline: `1px solid ${tokens.colors.brand.blue["500"]}`,
    boxShadow: "0px 0px 0px 1px rgba(163, 163, 163, 0.50)",
};

const errorState = {
    outline: `1px solid ${tokens.colors.ui.destructive}`,
    boxShadow: "0px 0px 1px 1px rgba(196, 61, 81, 0.20)",
};

const variantConfig = {
    primary: {
        background: tokens.colors.ui.primary,
        color: tokens.colors.ui["primary-foreground"],
        hover: {
            borderColor: tokens.colors.ui.primary,
        },
        active: {
            borderColor: tokens.colors.ui.primary,
        },
    },

    secondary: {
        background: tokens.colors.brand.blue["100"],
        color: tokens.colors.brand["aie-blue"]["500"],
        hover: {
            opacity: 0.8,
        },
        active: {
            opacity: 0.8,
        },
    },

    destructive: {
        background: tokens.colors.states.alert["50"],
        color: tokens.colors.ui.destructive,
        hover: {
            background: tokens.colors.states.alert["100"],
        },
        active: {
            background: tokens.colors.states.alert["50"],
        },
    },
};

const getVariantStyles = (variant) => {
    const config =
        variantConfig[variant] || variantConfig.primary;

    const {
        background,
        color,
        hover,
        active,
    } = config;

    return {
        background,
        color,
        hover,
        active,
    };
};

const HiddenRadio = styled.input.attrs({
    type: "radio",
})({
    position: "absolute",
    width: "1px",
    height: "1px",
    margin: "-1px",
    padding: 0,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
    border: 0,
});

const RadioWrapper = styled.label(({ $size = "lg" }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: sizes[$size].gap,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: sizes[$size].fontSize,
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: sizes[$size].lineHeight,
    color: tokens.colors.ui.foreground,
    cursor: "pointer",

    "&:has(input:disabled)": {
        opacity: 0.5,
        cursor: "not-allowed",
    },

    "input:focus-visible + span": {
        ...defaultFocusVisibleState,
    },

    'input[data-error="true"] + span': {
        ...errorState,
    },
}));

const OuterCircle = styled.span(
    ({
        $size = "lg",
        $variant = "primary",
        $checked = false,
    }) => {
        const { outer } = sizes[$size];
        const variantStyles = getVariantStyles($variant);

        return {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            width: outer,
            height: outer,
            borderRadius: "50%",
            border: `2px solid ${$checked
                    ? variantStyles.background
                    : tokens.colors.ui.border
                }`,
            background: $checked
                ? variantStyles.background
                : tokens.colors.ui.background,
            transition: "all 0.2s ease",

            "&:hover": {
                ...variantStyles.hover,
            },

            "&:active": {
                ...variantStyles.active,
            },
        };
    }
);

const InnerDot = styled.span(
    ({
        $size = "lg",
        $variant = "primary",
        $checked = false,
    }) => {
        const { inner } = sizes[$size];
        const variantStyles = getVariantStyles($variant);

        return {
            width: inner,
            height: inner,
            borderRadius: "50%",
            background: variantStyles.color,
            transition: "all 0.2s ease",
            opacity: $checked ? 1 : 0,
            transform: $checked
                ? "scale(1)"
                : "scale(0)",
        };
    }
);

const LabelContainer = styled.span({
    display: "flex",
    flexDirection: "column",
});

const LabelText = styled.span(({ $error = false }) => ({
    color: $error
        ? tokens.colors.ui.destructive
        : tokens.colors.ui.foreground,
}));

const Subtext = styled.span({
    fontSize: tokens.typography.fontSize["md-12px"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "16px",
    color: tokens.colors.ui["muted-foreground"],
});

/**
 * A radio button component with support for multiple variants, sizes, and states.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.size="lg"] - Size variant: "xs", "sm", "md", or "lg"
 * @param {string} [props.variant="primary"] - Visual variant: "primary", "secondary", or "destructive"
 * @param {boolean} [props.checked=false] - Whether the radio is selected
 * @param {boolean} [props.disabled=false] - If true, the radio is disabled
 * @param {boolean} [props.error=false] - If true, applies error styling
 * @param {string} [props.label] - Label text to display next to the radio
 * @param {string} [props.subtext] - Subtext to display below the label
 * @param {function} [props.onChange] - Callback fired when the radio state changes
 * @param {React.Ref} ref - Forwarded ref to the underlying input element
 * @returns {JSX.Element} The rendered radio component
 */
const Radio = forwardRef(
    (
        {
            size = "lg",
            variant = "primary",
            checked = false,
            disabled = false,
            error = false,
            label,
            subtext,
            onChange,
            ...props
        },
        ref
    ) => {
        const handleChange = (e) => {
            if (disabled) {
                return;
            }

            if (onChange) {
                onChange(e);
            }
        };

        return (
            <RadioWrapper $size={size}>
                <HiddenRadio
                    ref={ref}
                    checked={checked}
                    disabled={disabled}
                    onChange={handleChange}
                    data-error={
                        error ? "true" : undefined
                    }
                    {...props}
                />

                <OuterCircle
                    $size={size}
                    $variant={variant}
                    $checked={checked}
                >
                    <InnerDot
                        $size={size}
                        $variant={variant}
                        $checked={checked}
                    />
                </OuterCircle>

                {(label || subtext) && (
                    <LabelContainer>
                        {label && (
                            <LabelText $error={error}>
                                {label}
                            </LabelText>
                        )}

                        {subtext && (
                            <Subtext>
                                {subtext}
                            </Subtext>
                        )}
                    </LabelContainer>
                )}
            </RadioWrapper>
        );
    }
);

Radio.displayName = "Radio";

export default Radio;